import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSaveForum } from '@/api/information';
import { PrimaryButton } from '@/components/common/Button/style';
import Field from '@/components/common/Field/Field';
import InputFiles, { IPastData } from '@/components/common/Input/InputFiles/InputFiles';
import InputText from '@/components/common/Input/InputText/InputText';
import InputTextarea from '@/components/common/Input/InputTextarea/InputTextarea';
import { INFORMATION_MENU_COMMUNITY, INFORMATION_STEP_EDIT, normalizeInformationStep } from '@/constants/information';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';

import * as S from './style';

interface FormData {
	title: string;
	content: string;
}
interface FormErrors {
	title?: boolean;
	content?: boolean;
}

const InformationCommunityForm = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const selectedStep = normalizeInformationStep(location.state?.step);
	const selectedData = location.state?.data ?? {};
	const userStore = useAuthStore();
	const [formData, setFormData] = useState<FormData>({ title: '', content: '' });
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
	const [pastFiles, setPastFiles] = useState<IPastData[]>([]);
	const [errors, setErrors] = useState<FormErrors>({});
	const [submitted, setSubmitted] = useState(false);
	const [deletePk, setDeletePk] = useState<number[]>([]);

	const { openToast } = useToastStore();
	const navigate = useNavigate();
	const { mutateAsync: saveForum } = useSaveForum();

	const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });

		if (submitted) {
			const newErrors = { ...errors };
			if (name === 'title' && value) delete newErrors.title;
			if (name === 'content' && value) delete newErrors.content;
			setErrors(newErrors);
		}
	};

	const validateForm = (): FormErrors => {
		const newErrors: FormErrors = {};
		if (!formData.title) newErrors.title = true;
		if (!formData.content) newErrors.content = true;
		return newErrors;
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formErrors = validateForm();
		setErrors(formErrors);
		setSubmitted(true);

		if (Object.keys(formErrors).length > 0) return;

		const form = new FormData();
		if (selectedStep === INFORMATION_STEP_EDIT) form.append('forum_pk', selectedData.pk);
		form.append('member_pk', userStore.pk);
		form.append('forum_title', formData.title);
		form.append('forum_content', formData.content);
		uploadedFiles.forEach((file, index) => {
			form.append(`file[${index}]`, file);
		});
		deletePk.forEach((pk, index) => {
			form.append(`delete_file_pk[${index}]`, String(pk));
		});

		try {
			const res = await saveForum(form);
			if (res.data.success) {
				openToast({ icon: 'alert', content: t('information.form.toastSaved'), showTime: 3000 });
				setTimeout(() => navigate('/information', { state: { menu: INFORMATION_MENU_COMMUNITY } }), 3000);
			} else {
				openToast({ icon: 'alert', content: t('제출에 실패하였습니다.'), showTime: 3000 });
			}
		} catch (error) {
			console.error('Error:', error);
		}

		setFormData({ title: '', content: '' });
		setSubmitted(false);
	};

	const deletePastFileData = (pk: number) => {
		setDeletePk([...deletePk, pk]);
		setPastFiles(pastFiles.filter((item) => item.filePk !== pk));
	};

	useEffect(() => {
		if (selectedStep !== INFORMATION_STEP_EDIT) return;

		setFormData({
			title: selectedData.title,
			content: selectedData.content,
		});
		const tempFileList = selectedData.files.map((item: { file_original_name: string; file_size: number; file_pk: number }) => ({
			fileName: item.file_original_name,
			fileSize: item.file_size,
			filePk: item.file_pk,
		}));
		setPastFiles(tempFileList);
	}, [selectedData, selectedStep]);

	return (
		<S.FormPage>
			<S.Form onSubmit={handleSubmit} noValidate>
				<Field title="information.form.title" error={submitted && errors.title ? 'information.form.validation.title' : ''}>
					<InputText
						name="title"
						placeholder="information.form.titlePlaceholder"
						value={formData.title}
						input={handleChange}
						isError={submitted && !!errors.title}
					/>
				</Field>

				<Field title="information.form.content" error={submitted && errors.content ? 'information.form.validation.content' : ''}>
					<InputTextarea
						name="content"
						placeholder="information.form.contentPlaceholder"
						value={formData.content}
						input={handleChange}
						isError={submitted && !!errors.content}
						maxLength={2000}
						minHeight="38.4rem"
					/>
				</Field>

				<Field title="information.form.attachment">
					<InputFiles files={uploadedFiles} setFiles={setUploadedFiles} pastData={pastFiles} pastDataDeleteAction={deletePastFileData} />
				</Field>

				<S.FormActions>
					<PrimaryButton type="submit" disabled={!formData.title || !formData.content}>
						{selectedStep === INFORMATION_STEP_EDIT ? t('information.form.submitEdit') : t('information.form.submitCreate')}
					</PrimaryButton>
				</S.FormActions>
			</S.Form>
		</S.FormPage>
	);
};

export default InformationCommunityForm;
